import React, { useState, useEffect, useCallback } from 'react';
import {
    RefreshCw, TrendingUp, TrendingDown, Minus,
    AlertTriangle, CheckCircle2, Bot, Calculator,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchDailyReport } from '../api/analyticsApi';

/**
 * Daily Report — auto-generated each day, compared period-over-period.
 *
 * Reads the snapshot-backed `/analytics/daily-report/` endpoint, which returns
 * the current period, the previous equal-length period, and rules-based
 * "what to improve" insights. The user switches the comparison window
 * (week / month / quarter / 6 months / year) with the segmented control.
 *
 * WHAT THIS PANEL DOES NOT SHOW, DELIBERATELY (Monday 13037765152, Christian:
 * "Move the key metrics into the top overview & remove any duplicates. We dont
 * need 2 of them on the report page"). It used to open with its own "Key
 * metrics" grid, a DOMA commission banner, a conversion funnel and a traffic
 * source list — every one of which the same page already rendered above or
 * below it, from the LIVE endpoints on the page's own date range. Two copies of
 * a number that are computed differently will disagree, and this page had four
 * such pairs, which is what made the analytics look broken. Each one now lives
 * in exactly one place:
 *
 *   key metrics      → KPISummaryCards (Overview, with the deltas moved across)
 *   DOMA commission  → DomaInfluencedRevenue (Overview)
 *   conversion funnel→ FunnelChart (Buyer journey & traffic)
 *   traffic sources  → SourceQualityChart (Buyer journey & traffic)
 *
 * What is left here is what only the nightly snapshots can answer: GeeVee and
 * calculator usage over a long window, and the rules-based insights. Do not add
 * a metric card back without checking it is not already on the page.
 */

const PERIODS = [
    { key: 'day', label: 'Daily' },
    { key: 'week', label: 'Weekly' },
    { key: 'month', label: 'Monthly' },
    { key: 'quarter', label: 'Quarterly' },
    { key: '6month', label: '6 Months' },
    { key: 'year', label: 'Annual' },
];

const PREV_LABEL = {
    day: 'prior day', week: 'prior week', month: 'prior month',
    quarter: 'prior quarter', '6month': 'prior 6 months', year: 'prior year',
};

// Noun used in "this {X}" headings — keeps grammar clean for multi-word periods.
const CUR_LABEL = {
    day: 'day', week: 'week', month: 'month',
    quarter: 'quarter', '6month': '6 months', year: 'year',
};

// ── formatters ───────────────────────────────────────────────────────────────
const fmtNum = (n) => {
    if (n == null) return '—';
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
    return `${Math.round(n * 100) / 100}`;
};
const fmtPct = (n) => (n == null ? '—' : `${(Math.round(n * 10) / 10).toFixed(1)}%`);
const fmtMoneyK = (n) => {
    if (n == null) return '—';
    if (Math.abs(n) >= 1000) return `$${Math.round(n / 1000)}K`;
    return `$${Math.round(n)}`;
};
const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

// Delta chip showing change vs previous period.
const Delta = ({ metric, format = fmtNum, invert = false }) => {
    if (!metric) return null;
    const { previous, change_pct, direction } = metric;
    // up = green, down = red, flat = gray (mockup convention). `invert` flips it
    // for metrics where a smaller number is the win (none used today, kept for clarity).
    const good = invert ? direction === 'down' : direction === 'up';
    const bad = invert ? direction === 'up' : direction === 'down';
    const color = good ? 'text-emerald-600' : bad ? 'text-orange-600' : 'text-gray-400';
    const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
            <Icon className="w-3.5 h-3.5" />
            {change_pct != null ? `${change_pct > 0 ? '+' : ''}${change_pct}%` : `was ${format(previous)}`}
        </span>
    );
};

// Split bar showing the dominant audience share (e.g. "Owner-occupier 99%").
const MixBar = ({ primaryLabel, primaryPct, green = false }) => {
    const pct = Math.max(0, Math.min(100, primaryPct || 0));
    return (
        <div className="flex gap-1.5 h-4">
            <div
                className={`rounded flex items-center px-2 text-[11px] font-semibold ${
                    green ? 'bg-emerald-400 text-emerald-900' : 'bg-gray-300 text-gray-800'
                }`}
                style={{ flex: Math.max(pct, 1), minWidth: 0 }}
            >
                <span className="truncate">{primaryLabel}</span>
            </div>
            <div
                className={`rounded ${green ? 'bg-emerald-200' : 'bg-gray-200'}`}
                style={{ flex: Math.max(100 - pct, 1) }}
            />
        </div>
    );
};

const DailyReportPanel = () => {
    const [period, setPeriod] = useState('week');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async (p) => {
        setLoading(true);
        setError(false);
        try {
            const res = await fetchDailyReport(p);
            setData(res);
        } catch (e) {
            setError(true);
            toast.error('Failed to load daily report');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(period); }, [period, load]);

    const m = data?.metrics || {};
    const prevLabel = PREV_LABEL[period] || 'previous period';
    const curLabel = CUR_LABEL[period] || 'period';

    return (
        <div>
            {/* Period selector + range caption */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div className="flex bg-gray-100 p-1 rounded-xl overflow-x-auto">
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            onClick={() => setPeriod(p.key)}
                            className={`flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${
                                period === p.key
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                {data && (
                    <div className="text-xs text-gray-500">
                        {fmtDate(data.current_start)} – {fmtDate(data.current_end)}
                        <span className="text-gray-400"> vs {prevLabel} ({fmtDate(data.previous_start)} – {fmtDate(data.previous_end)})</span>
                    </div>
                )}
            </div>

            {loading && !data ? (
                <div className="flex items-center justify-center py-24">
                    <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
            ) : error ? (
                <div className="text-center py-16 text-gray-500">
                    Could not load the daily report. Try refreshing.
                </div>
            ) : !data?.has_data ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                    <p className="text-sm text-amber-800 font-medium">No snapshot data for this period yet.</p>
                    <p className="text-xs text-amber-700 mt-1">
                        Daily snapshots are generated each night. Run the backfill command to populate history,
                        or pick a shorter window.
                    </p>
                </div>
            ) : (
                <div className={`transition-opacity ${loading ? 'opacity-50' : ''}`}>
                    {/* Section 1 — GeeVee + calculator */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                                <Bot className="w-4 h-4" /> GeeVee chatbot
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <div className="text-[11px] text-gray-500">Sessions</div>
                                    <div className="text-lg font-semibold text-gray-900">{fmtNum(m.geevee_sessions?.current)}</div>
                                    <Delta metric={m.geevee_sessions} />
                                </div>
                                <div>
                                    <div className="text-[11px] text-gray-500">Completion</div>
                                    <div className="text-lg font-semibold text-gray-900">{fmtPct(m.geevee_completion?.current)}</div>
                                    <Delta metric={m.geevee_completion} format={fmtPct} />
                                </div>
                                <div>
                                    <div className="text-[11px] text-gray-500">Leads</div>
                                    <div className={`text-lg font-semibold ${m.geevee_leads?.current ? 'text-gray-900' : 'text-orange-600'}`}>
                                        {fmtNum(m.geevee_leads?.current)}
                                    </div>
                                    <Delta metric={m.geevee_leads} />
                                </div>
                            </div>
                            <div className="text-[11px] text-gray-400 mt-3">
                                {fmtNum(m.geevee_messages?.current)} messages exchanged this {curLabel}
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-5">
                            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                                <Calculator className="w-4 h-4" /> Calculator usage
                            </div>
                            {(() => {
                                const inv = data.calculator_stats?.investment || {};
                                const mort = data.calculator_stats?.mortgage || {};
                                return (
                                    <div className="space-y-4">
                                        {/* Mortgage */}
                                        <div>
                                            <div className="flex items-baseline justify-between mb-1.5">
                                                <span className="text-xs font-semibold text-gray-800">Mortgage calculator</span>
                                                <span className="text-[11px] text-gray-500">
                                                    {fmtNum(m.mortgage_calc_runs?.current)} runs <Delta metric={m.mortgage_calc_runs} />
                                                </span>
                                            </div>
                                            <MixBar
                                                primaryLabel={`Owner-occupier ${mort.owner_occupier_pct ?? 0}%`}
                                                primaryPct={mort.owner_occupier_pct ?? 0}
                                            />
                                            <div className="text-[11px] text-gray-400 mt-1">
                                                {fmtNum(mort.calculations)} calcs · avg property {fmtMoneyK(mort.avg_property_price)} · avg loan {fmtMoneyK(mort.avg_loan_amount)}
                                            </div>
                                        </div>
                                        {/* Investment */}
                                        <div>
                                            <div className="flex items-baseline justify-between mb-1.5">
                                                <span className="text-xs font-semibold text-gray-800">Investment calculator</span>
                                                <span className="text-[11px] text-gray-500">
                                                    {fmtNum(m.investment_calc_runs?.current)} runs <Delta metric={m.investment_calc_runs} />
                                                </span>
                                            </div>
                                            <MixBar
                                                primaryLabel={`Single property ${inv.single_pct ?? 0}%`}
                                                primaryPct={inv.single_pct ?? 0}
                                                green
                                            />
                                            <div className="text-[11px] text-gray-400 mt-1">
                                                {fmtNum(inv.calculations)} calcs · avg gross yield {fmtPct(inv.avg_gross_yield_pct)} · avg net {fmtPct(inv.avg_net_yield_pct)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Section 2 — insights */}
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">
                        What we need to improve on
                    </div>
                    <div className="space-y-2.5">
                        {(data.insights || []).map((ins, i) => {
                            const warn = ins.tone === 'warn';
                            return (
                                <div
                                    key={i}
                                    className={`rounded-r-lg p-3.5 border-l-[3px] ${
                                        warn ? 'bg-orange-50 border-orange-500' : 'bg-emerald-50 border-emerald-500'
                                    }`}
                                >
                                    <div className={`text-[11px] font-semibold mb-1 flex items-center gap-1.5 ${
                                        warn ? 'text-orange-700' : 'text-emerald-700'
                                    }`}>
                                        {warn ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                        {ins.label}
                                    </div>
                                    <div className="text-sm text-gray-700 leading-relaxed">{ins.text}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-[11px] text-gray-400 mt-5">
                        Snapshots cover {data.days_with_data} day{data.days_with_data === 1 ? '' : 's'} in this window •
                        generated nightly from buyer-portal activity.
                    </div>
                </div>
            )}
        </div>
    );
};

export default DailyReportPanel;
